import { useCallback, useEffect, useRef } from "react";
import {
  DEFAULT_JOURNEY_STATE,
  RegistrationJourneyState,
  useRegistrationJourneyContext,
} from "./RegistrationJourneyContext";
import {
  generatePath,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router";

type SetStepPayload =
  | { stepIndex: 1; selectedExam: RegistrationJourneyState["selectedExam"] }
  | { stepIndex: 2; termsAccepts: RegistrationJourneyState["termsAccepts"] }
  | {
      stepIndex: 3;
      candidateDetails: RegistrationJourneyState["candidateDetails"];
      marketingConsent: RegistrationJourneyState["marketingConsent"];
    }
  | ({ stepIndex: number } & Partial<RegistrationJourneyState>);

export const JOURNEY_STEPS = [
  { url: "select-exam" },
  { url: "terms" },
  { url: "candidate-details" },
  { url: "summary" },
  { url: "confirmation/:guid" },
];

export const getJourneyStepUrl = (stepIndex: number, arg?: object) =>
  generatePath("/" + JOURNEY_STEPS[stepIndex].url, arg);

export const useRegistrationJourney = (stepValidation: boolean = true) => {
  const initialized = useRef(false);
  const { journey, setJourney } = useRegistrationJourneyContext();
  const loadedFromStorage = journey.loadedFromStorage;
  const navigate = useNavigate();
  const location = useLocation();

  const setStep = useCallback(
    ({ stepIndex, ...payload }: SetStepPayload) => {
      const highestStepIndex = getHighestStepIndex({ ...journey, ...payload });

      const targetStepIndex =
        stepIndex > highestStepIndex ? highestStepIndex : stepIndex;
      const stepUrl = getJourneyStepUrl(targetStepIndex);

      if (targetStepIndex > 0) {
        setJourney((prev) => ({
          ...prev,
          ...payload,
          stepIndex: targetStepIndex,
        }));
      } else {
        setJourney((state) => ({
          ...DEFAULT_JOURNEY_STATE,
          loadedFromStorage: state.loadedFromStorage,
        }));
      }

      if (!matchPath(stepUrl, location.pathname)) {
        navigate(stepUrl);
      }
    },
    [journey, setJourney, location.pathname, navigate]
  );

  useEffect(() => {
    if (initialized.current || !loadedFromStorage || !stepValidation) {
      return;
    }

    const stepIndex = JOURNEY_STEPS.findIndex(
      (_, i) => !!matchPath(getJourneyStepUrl(i), location.pathname)
    );

    setStep({ stepIndex });
    initialized.current = true;
  }, [location.pathname, setStep, stepValidation, loadedFromStorage]);

  return {
    stepIndex: journey.stepIndex,
    selectedExam: journey.selectedExam,
    termsAccepts: journey.termsAccepts,
    candidateDetails: journey.candidateDetails,
    marketingConsent: journey.marketingConsent,
    hasNextStep: journey.stepIndex < JOURNEY_STEPS.length - 2, // Exclude confirmation step
    setStep: setStep,
    selectExam: (selectedExam: RegistrationJourneyState["selectedExam"]) => {
      setStep({ stepIndex: 1, selectedExam });
    },
    acceptTerms: (termsAccepts: RegistrationJourneyState["termsAccepts"]) => {
      setStep({ stepIndex: 2, termsAccepts });
    },
    provideCandidateDetails: (
      candidateDetails: RegistrationJourneyState["candidateDetails"],
      marketingConsent: RegistrationJourneyState["marketingConsent"]
    ) => {
      setStep({ stepIndex: 3, candidateDetails, marketingConsent });
    },
    confirmRegistration: (guid: string) => {
      navigate(getJourneyStepUrl(JOURNEY_STEPS.length - 1, { guid }));
      setJourney((state) => ({
        ...DEFAULT_JOURNEY_STATE,
        loadedFromStorage: state.loadedFromStorage,
      }));
    },
    cancel: () => {
      setStep({
        stepIndex: 0,
        selectedExam: undefined,
        termsAccepts: undefined,
        candidateDetails: undefined,
      });
    },
  };
};

export const getHighestStepIndex = (journey: RegistrationJourneyState) => {
  const { selectedExam, termsAccepts, candidateDetails } = journey;

  if (selectedExam && termsAccepts && candidateDetails) {
    return 3;
  } else if (selectedExam && termsAccepts) {
    return 2;
  } else if (selectedExam) {
    return 1;
  }
  return 0;
};
